import { useMutation, useQuery, useQueryClient } from 'react-query';
import { httpRequest } from '../config/api';
import { withUploadToast } from '../utils/uploadToast';

const correlationConfig = () => ({
  headers: {
    'X-Correlation-ID': globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`
  }
});

// Exported like `jurisdictionService`: the top bar has to read a period's files outside React
// Query's hooks, to answer "which file is this one, over there?" before it navigates.
export const periodService = {
  createPeriod: data => httpRequest.post('api/workflows/periods', data),
  createAIFMPeriod: data => httpRequest.post(`api/aifm/period/${data.id}`, data.body),
  createPeriodAutomatic: data => httpRequest.post('api/workflows/periods/automatic', data),
  convertAutomationReport: ({ periodId, workflowId }) =>
    httpRequest.post(
      `${periodId}/conversion/parse`,
      {},
      {
        headers: {
          workflow_id: workflowId
        }
      }
    ),
  updatePeriod: ({ periodId, data }) => httpRequest.put(`api/workflows/periods/${periodId}`, data),
  getPeriodFiles: (periodId, params) =>
    httpRequest.get(`api/periods/${periodId}/files`, { params }),
  getWorkflowsPeriods: periodId => httpRequest.get(`api/workflows/periods/${periodId}`),
  getPeriodById: (periodId, params) =>
    httpRequest.get(`api/workflows/periods/${periodId}`, { ...params }),
  deletePeriodById: periodId => httpRequest.delete(`api/workflows/periods/${periodId}`),
  deletePeriodFile: data => httpRequest.delete(`api/periods/${data.periodId}/files/${data.fileId}`),
  mutateTransformationNote: ({ data, transformId, stepId }) =>
    httpRequest.put(`api/${transformId}/edit-note/${stepId}`, data, correlationConfig()),
  uploadFiles: ({ periodId, data, onUploadProgress }) =>
    httpRequest.post(`api/periods/${periodId}/files`, data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress
    }),
  updateUploadedFile: ({ periodId, fileId, data, onUploadProgress }) =>
    httpRequest.put(`api/periods/${periodId}/files/${fileId}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress
    }),
  reRunAiActions: ({ transformationId, stepId, data }) =>
    httpRequest.post(
      `/api/retry-smart-transformation/${transformationId}/${stepId}`,
      data,
      correlationConfig()
    ),
  // Fetches the instrument reference data for one 'Enrich instrument data' step and returns the
  // transformation with those columns already joined in.
  enrichInstrumentData: ({ transformationId, stepId }) =>
    httpRequest.post(`/api/enrich-instrument-data/${transformationId}/${stepId}`)
};

export const useCreatePeriod = ({ mutationOptions } = {}) => {
  return useMutation(periodService.createPeriod, mutationOptions);
};

export const useCreateAIFMPeriod = ({ mutationOptions } = {}) => {
  return useMutation(periodService.createAIFMPeriod, mutationOptions);
};

export const useCreatePeriodAutomatic = ({ mutationOptions } = {}) => {
  return useMutation(periodService.createPeriodAutomatic, mutationOptions);
};

export const useDeletePeriodFile = ({ mutationOptions } = {}) => {
  return useMutation(periodService.deletePeriodFile, mutationOptions);
};

export const useDeletePeriod = ({ mutationOptions } = {}) => {
  return useMutation(periodService.deletePeriodById, mutationOptions);
};

/**
 * @param {import('./serviceHooks.types').ServiceQueryArg} [options]
 */
export const usePeriodFiles = ({ queryOptions, periodId, params = {} } = {}) => {
  return useQuery(
    ['GET_PERIOD_FILES', periodId, params],
    () => periodService.getPeriodFiles(periodId, params),
    queryOptions
  );
};

// Same endpoint as `usePeriodById`, so the same cache entry.
export const useGetWorklflowsPeriods = ({ queryOptions, periodId } = {}) => {
  return useQuery(
    ['GET_PERIOD_BY_ID', '', periodId],
    () => periodService.getWorkflowsPeriods(periodId),
    queryOptions
  );
};

/**
 * @param {import('./serviceHooks.types').ServiceQueryArg} [options]
 */
export const usePeriodById = ({
  queryOptions,
  queryParams,
  periodId,
  customKey = '',
  params = {}
} = {}) => {
  return useQuery(
    ['GET_PERIOD_BY_ID', customKey, periodId],
    () => periodService.getPeriodById(periodId, params),
    queryParams ?? queryOptions
  );
};

export const useConvertReport = ({ mutationOptions } = {}) => {
  return useMutation(periodService.convertAutomationReport, mutationOptions);
};

export const useUploadPeriodFiles = ({ mutationOptions } = {}) => {
  const queryClient = useQueryClient();
  return useMutation(periodService.uploadFiles, withUploadToast(queryClient, mutationOptions));
};

export const useUpdateUploadedFile = ({ mutationOptions } = {}) => {
  const queryClient = useQueryClient();
  return useMutation(
    periodService.updateUploadedFile,
    withUploadToast(queryClient, mutationOptions)
  );
};

export const useUpdatePeriod = () => {
  return useMutation(periodService.updatePeriod);
};

export const useMutateTransformationNote = ({ mutationOptions } = {}) => {
  return useMutation(periodService.mutateTransformationNote, mutationOptions);
};

export const useReRunAiActions = ({ mutationOptions } = {}) => {
  return useMutation(periodService.reRunAiActions, mutationOptions);
};

export const useEnrichInstrumentData = ({ mutationOptions } = {}) => {
  return useMutation(periodService.enrichInstrumentData, mutationOptions);
};
